'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useVenueAvailabilityStore } from '@/entities/venue';
import { getFormattedVenueOpeningHours } from '@/entities/venue/lib/get-formatted-venue-opening-hours.util';
import { getVenueAvailabilityStatus } from '@/entities/venue/lib/venue-availability.utils';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { QueryApi } from '@/features/query/api';
import { SelectVenueModalContext } from './SelectVenueModalProvider';
import { SelectVenueModalVenue } from './SelectVenueModalVenue';
import './styles.scss';

export const SelectVenueModalVenuesList = () => {
    const t = useTranslations();
    const { formMethods, isImageVisible, availableForItemId, allowSelectCurrent } =
        useContext(SelectVenueModalContext);
    const [deliveryType, venues, showFillUpAddress] = useWatch({
        control: formMethods.control,
        name: ['deliveryType', 'deliveryVenues', 'pickupVenues', 'address'],
        compute: ([deliveryType, deliveryVenues, pickupVenues, address]) => {
            const showFillUpAddress =
                deliveryType === DeliveryType.DELIVERY && address !== null && !address?.street;
            return [
                deliveryType,
                deliveryType === DeliveryType.DELIVERY ? deliveryVenues : pickupVenues,
                showFillUpAddress,
            ] as const;
        },
    });

    const { data: items } = useQuery({
        ...QueryApi.MenuList.queries.AggregatedMenuItems,
        enabled: !!availableForItemId,
    });

    const filteredVenues = useMemo(() => {
        let filteredVenues = [...venues];
        if (allowSelectCurrent === false) {
            filteredVenues = filteredVenues.filter((v) => !v.isCurrent);
        }
        if (!availableForItemId) {
            return filteredVenues;
        }
        const item = items?.find((i) => i.itemId === availableForItemId);
        if (!item) return [];
        const allowedIds = new Set(item.availableInVenueIds);
        return filteredVenues.filter((v) => allowedIds.has(v.id));
    }, [availableForItemId, venues, items, allowSelectCurrent]);

    const storeStatus = useVenueAvailabilityStore((s) => s.status);
    const storeOpeningHours = useVenueAvailabilityStore((s) => s.formattedOpeningHours);

    if (venues.length === 1 && filteredVenues.length === 0) {
        return (
            <p
                className="select-venue-modal__no-venues"
                data-testid="select-venue-modal-select-address-from-suggestions-text"
            >
                {t('SELECT_VENUE_MODAL.ONLY_CURRENT_VENUE_DELIVERS')}
            </p>
        );
    }

    if (showFillUpAddress) {
        return (
            <p
                className="select-venue-modal__no-venues"
                data-testid="select-venue-modal-select-address-from-suggestions-text"
            >
                {t('SELECT_DELIVERY_BOX.SELECT_ADDRESS_FROM_SUGGESTIONS')}
            </p>
        );
    }

    if (filteredVenues.length === 0) {
        return (
            <p className="select-venue-modal__no-venues" data-testid="select-venue-modal-no-venues">
                {t('SELECT_VENUE_MODAL.NO_VENUES')}
            </p>
        );
    }

    return filteredVenues.map((venue) => {
        const openingHours = venue.isCurrent
            ? storeOpeningHours
            : getFormattedVenueOpeningHours(venue.openingHours ?? [], t);
        const status = venue.isCurrent
            ? storeStatus
            : getVenueAvailabilityStatus({
                  openingHours,
                  // TODO: outages are not loaded for non-current venues yet — modal status ignores foreign-venue outages
                  outages: [],
              });
        return (
            <SelectVenueModalVenue
                key={venue.apiPartnerSettingsId}
                id={venue.id}
                apiPartnerSettingsId={venue.apiPartnerSettingsId}
                title={venue.name}
                url={venue.url}
                address={{
                    street: venue.address.street,
                    postCode: venue.address.postCode,
                    city: venue.address.city,
                    country: venue.address.country,
                    lat: venue.address.lat ?? '0',
                    lon: venue.address.lon ?? '0',
                }}
                status={{
                    openingHours,
                    status,
                }}
                imageUrl={
                    isImageVisible ? venue.assets.find((a) => a.flags.includes('LOGO'))?.fullUrl : undefined
                }
                isSelected={venue.isCurrent}
                deliveryType={deliveryType}
            />
        );
    });
};
